import { t } from '@trimble/i18n';
import { AlignmentNavigationCapability } from '@trimble/web3d-capabilities';
import { createSignal, createEffect, JSX, Show, onMount } from 'solid-js';
import { AlignmentNavigationStatus } from '@technology/web3d-plugin-trimbim';
import { AlignmentNavigationTab } from './AlignmentNavigationTab';
import { AlignmentNavigationDialog } from './AlignmentNavigationDialog';
import { UvIconButton } from '../button/UvIconButton';
import { AlignmentNavigationInstructionsPanel } from './AlignmentNavigationInstructionsPanel';
import { LengthUnit } from '@fulcrum/lib';

export const AlignmentNavigationToolbar = (props: {
  alignmentNavigationCapability: AlignmentNavigationCapability | undefined;
  lengthUnit?: LengthUnit;
  lengthDecimals?: number;
}): JSX.Element => {
  const [activeChild, setActiveChild] = createSignal<AlignmentNavigationTab>(
    AlignmentNavigationTab.None
  );

  const handleDialogToggle = (): void => {
    if (activeChild() === AlignmentNavigationTab.Dialog) {
      setActiveChild(AlignmentNavigationTab.None);
    } else {
      // Exclusive: if instructions is open, close it and open dialog
      setActiveChild(AlignmentNavigationTab.Dialog);
    }
  };

  const handleInstructionsToggle = (): void => {
    if (activeChild() === AlignmentNavigationTab.Instructions) {
      setActiveChild(AlignmentNavigationTab.None);
    } else {
      // Exclusive: if dialog is open, close it and open instructions
      setActiveChild(AlignmentNavigationTab.Instructions);
    }
  };

  // Fix for modus-wc-divider height issue - remove vertical margins
  const fixDividerStyle = (): void => {
    const styleId = 'fix-modus-divider-height';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        modus-wc-divider[orientation="vertical"].hydrated {
          width: 2px !important;
          min-width: 2px !important;
          height: 100% !important;
          min-height: 32px !important;
          align-self: stretch !important;
          flex: 0 0 2px !important;
        }
        modus-wc-divider[orientation="vertical"].hydrated .modus-wc-divider-vertical {
          margin-top: 0 !important;
          margin-bottom: 0 !important;
          height: 32px !important;
          min-height: 32px !important;
        }
        modus-wc-divider[orientation="vertical"].hydrated .modus-wc-divider-vertical::before,
        modus-wc-divider[orientation="vertical"].hydrated .modus-wc-divider-vertical::after {
          height: 32px !important;
          min-height: 32px !important;
        }
      `;
      document.head.appendChild(style);
    }
  };
  onMount(fixDividerStyle);

  createEffect(() => {
    if (
      props.alignmentNavigationCapability?.status() ===
      AlignmentNavigationStatus.Picking
    ) {
      setActiveChild(AlignmentNavigationTab.Dialog);
    }
  });

  return (
    <Show
      when={
        props.alignmentNavigationCapability?.status() ===
          AlignmentNavigationStatus.Picking ||
        props.alignmentNavigationCapability?.status() ===
          AlignmentNavigationStatus.Navigating
      }
    >
      <div
        role="toolbar"
        aria-label="toolbar"
        class={`flex items-center px-1 py-1 gap-1 rounded-sm bg-base-page shadow-[0_0_10px_0_rgba(0,0,0,0.2)] groupHoverReveal min-h-[32px]`}
      >
        <UvIconButton
          dataTestId="align-nav-dialog-button"
          type="toggle"
          tooltip={t(`TC.3D.AlignmentNavigationToolbar.DialogButtonTooltip`)}
          pressed={activeChild() === AlignmentNavigationTab.Dialog}
          icon="tc-icon-path-navigation"
          onClick={() => handleDialogToggle()}
          disabled={false}
        />
        <UvIconButton
          dataTestId="align-nav-instructions-button"
          type="toggle"
          tooltip={t(
            `TC.3D.AlignmentNavigationToolbar.${
              activeChild() === AlignmentNavigationTab.Instructions
                ? 'HideInstructions'
                : 'ShowInstructions'
            }Tooltip`
          )}
          pressed={activeChild() === AlignmentNavigationTab.Instructions}
          onClick={() => handleInstructionsToggle()}
          disabled={false}
          icon={'info'}
        />
        <modus-wc-divider color="tertiary" orientation="vertical" />
        <UvIconButton
          dataTestId="align-nav-lock-button"
          type="toggle"
          tooltip={
            props.alignmentNavigationCapability?.verticalPanOnly()
              ? t('TC.3D.AlignmentNavigationToolbar.UnlockPanningButtonTooltip')
              : t('TC.3D.AlignmentNavigationToolbar.LockPanningButtonTooltip')
          }
          pressed={props.alignmentNavigationCapability?.verticalPanOnly()}
          onClick={() =>
            void props.alignmentNavigationCapability?.toggleVerticalPanLock()
          }
          disabled={
            props.alignmentNavigationCapability?.status() !==
            AlignmentNavigationStatus.Navigating
          }
          icon={
            props.alignmentNavigationCapability?.verticalPanOnly()
              ? 'lock'
              : 'lock_open'
          }
        />

        <Show when={activeChild() === AlignmentNavigationTab.Dialog}>
          <AlignmentNavigationDialog
            alignmentNavigationCapability={props.alignmentNavigationCapability}
            isOpen={true}
            lengthUnit={props.lengthUnit}
            lengthDecimals={props.lengthDecimals}
          />
        </Show>
        {/* Instructions Panel */}
        <Show when={activeChild() === AlignmentNavigationTab.Instructions}>
          <AlignmentNavigationInstructionsPanel />
        </Show>
      </div>
    </Show>
  );
};
